import * as THREE from 'three';
import { ImpactBurst } from '../entities/ImpactBurst';
import { createMeteorResources, disposeMeteorResources, Meteor } from '../entities/Meteor';
import {
  FORMATION_CRATER_COUNT,
  getCraterTarget,
  Moon,
  STORY_CRATER_COUNT,
  type MoonMode,
} from '../entities/Moon';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import {
  OBSERVATION_FEATURES,
  ObservationScene,
  type ObservationFeatureId,
} from '../scenes/ObservationScene';
import { MOON_VIEW, STORY_CAMERA_DISTANCE } from '../scenes/moonComposition';
import { MoonSculptingScene, type MeteorSize } from '../scenes/MoonSculptingScene';
import { MoonOrbitScene } from '../scenes/MoonOrbitScene';
import { MAX_ORBIT_MOONS, MoonCollection } from './MoonCollection';
import { Dialogue } from '../systems/Dialogue';

type StoryStage = Exclude<MoonMode, 'smooth'> | 'smooth';
type FadeState = 'idle' | 'out' | 'in';
type MeteorWave = 'formation' | 'late';
type StoryLine = {
  stage: StoryStage;
  kicker: string;
  title: string;
  text: string;
};

const STORY: StoryLine[] = [
  {
    stage: 'character',
    kicker: '달의 이야기',
    title: '나는 달이야!',
    text: '안녕! 나는 달이야.',
  },
  {
    stage: 'character',
    kicker: '달의 이야기',
    title: '내 비밀을 알려줄게',
    text: '내가 어쩌다 이런 모양이 되었는지 알려줄게.',
  },
  {
    stage: 'smooth',
    kicker: '시간을 거슬러',
    title: '약 45억 년 전…',
    text: '약 45억 년 전…',
  },
  {
    stage: 'smooth',
    kicker: '첫 번째 모습',
    title: '매끈한 달',
    text: '그때의 나는 매끈한 얼굴이었어.',
  },
  {
    stage: 'impacts',
    kicker: '두 번째 이야기',
    title: '돌들이 날아왔어!',
    text: '그런데… 우주에서 날아온 돌들이 나를 쿵! 하고 때렸지.',
  },
  {
    stage: 'lava',
    kicker: '세 번째 이야기',
    title: '달의 바다가 생겼어',
    text: '부딪힌 곳은 움푹 패이고, 갈라진 틈으로 뜨거운 용암이 흘러나왔어.',
  },
  {
    stage: 'cratered',
    kicker: '마지막 모습',
    title: '울퉁불퉁한 달',
    text: '시간이 흐를수록 돌들은 계속 나를 때렸고, 나는 지금처럼 울퉁불퉁해졌어.',
  },
  {
    stage: 'cratered',
    kicker: '이제 직접 볼 차례',
    title: '진짜 달을 만나볼까?',
    text: '실제 내 모습을 봐볼래?',
  },
];

const STAGE_LINE_INDEX: Record<string, number> = {
  intro: 0,
  'active-play': 3,
  smooth: 3,
  impacts: 4,
  lava: 5,
  cratered: 6,
  complete: 7,
};

const FORMATION_METEOR_COUNT = 30;
const LATE_CRATER_REVEAL_COUNT = STORY_CRATER_COUNT - FORMATION_CRATER_COUNT;
const LATE_METEOR_COUNT = LATE_CRATER_REVEAL_COUNT * 2;
const METEOR_SHOWER_DIRECTION = new THREE.Vector3(-0.34, -0.22, -0.91).normalize();
const METEOR_SHOWER_SIDE = new THREE.Vector3().crossVectors(
  METEOR_SHOWER_DIRECTION,
  new THREE.Vector3(0, 1, 0),
).normalize();

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(40, 1, 0.1, 80);
  private readonly moon = new Moon();
  private readonly observation: ObservationScene;
  private readonly sculpting: MoonSculptingScene;
  private readonly orbitScene: MoonOrbitScene;
  private readonly moonCollection = new MoonCollection();
  private readonly dialogue = new Dialogue();
  private readonly meteors: Meteor[] = [];
  private readonly bursts: ImpactBurst[] = [];
  private readonly meteorResources = createMeteorResources();
  private readonly eraLabel = this.getElement<HTMLElement>('#era-label');
  private readonly dialoguePanel = this.getElement<HTMLElement>('#dialogue-panel');
  private readonly eraKicker = this.getElement('#era-kicker');
  private readonly eraValue = this.getElement('#era-value');
  private readonly app = this.getElement('#app');
  private readonly cta = this.getElement<HTMLElement>('#observation-cta');
  private readonly observeButton = this.getElement<HTMLButtonElement>('#observe-button');
  private readonly ctaNote = this.getElement<HTMLElement>('#cta-note');
  private readonly observationUi = this.getElement<HTMLElement>('#observation-ui');
  private readonly observationBackButton = this.getElement<HTMLButtonElement>('#observation-back');
  private readonly observationResetButton = this.getElement<HTMLButtonElement>('#observation-reset');
  private readonly observationStatus = this.getElement<HTMLElement>('#observation-status');
  private readonly observationHotspots = this.getElement<HTMLElement>('#observation-hotspots');
  private readonly observationFeatureModal = this.getElement<HTMLElement>('#observation-feature-modal');
  private readonly observationFeatureClose = this.getElement<HTMLButtonElement>('#observation-feature-close');
  private readonly observationFeatureTitle = this.getElement<HTMLElement>('#observation-feature-title');
  private readonly observationFeatureDialogue = this.getElement<HTMLElement>('#observation-feature-dialogue');
  private readonly observationFeaturePreview = this.getElement<HTMLCanvasElement>('#observation-feature-preview');
  private readonly mainMenuUi = this.getElement<HTMLElement>('#main-menu-ui');
  private readonly mainMenuHint = this.getElement<HTMLElement>('#main-menu-hint');
  private readonly mainMenuMoonButton = this.getElement<HTMLButtonElement>('#main-moon-button');
  private readonly mainMenuChoices = this.getElement<HTMLElement>('#main-menu-choices');
  private readonly formationChoiceButton = this.getElement<HTMLButtonElement>('#formation-choice');
  private readonly observationChoiceButton = this.getElement<HTMLButtonElement>('#observation-choice');
  private readonly gameChoiceButton = this.getElement<HTMLButtonElement>('#game-choice');
  private readonly moonGameUi = this.getElement<HTMLElement>('#moon-game-ui');
  private readonly moonGameGuide = this.getElement<HTMLElement>('#moon-game-guide');
  private readonly moonGameGuideNumber = this.getElement<HTMLElement>('#moon-game-guide-number');
  private readonly moonGameGuideTitle = this.getElement<HTMLElement>('#moon-game-guide-title');
  private readonly moonGameGuideText = this.getElement<HTMLElement>('#moon-game-guide-text');
  private readonly moonGameBackButton = this.getElement<HTMLButtonElement>('#moon-game-back');
  private readonly moonGameUndoButton = this.getElement<HTMLButtonElement>('#moon-game-undo');
  private readonly moonGameRedoButton = this.getElement<HTMLButtonElement>('#moon-game-redo');
  private readonly moonGameStatus = this.getElement<HTMLElement>('#moon-game-status');
  private readonly moonGameCraterCount = this.getElement<HTMLElement>('#moon-game-crater-count');
  private readonly moonGameSizeControls = this.getElement<HTMLElement>('#moon-game-size-controls');
  private readonly moonGameEruptButton = this.getElement<HTMLButtonElement>('#moon-game-erupt');
  private readonly moonGameFinishButton = this.getElement<HTMLButtonElement>('#moon-game-finish');
  private readonly moonGameExitModal = this.getElement<HTMLElement>('#moon-game-exit-modal');
  private readonly moonGameExitCancelButton = this.getElement<HTMLButtonElement>('#moon-game-exit-cancel');
  private readonly moonGameExitConfirmButton = this.getElement<HTMLButtonElement>('#moon-game-exit-confirm');
  private readonly moonFinishModal = this.getElement<HTMLElement>('#moon-finish-modal');
  private readonly moonFinishSummary = this.getElement<HTMLElement>('#moon-finish-summary');
  private readonly moonFinishCancelButton = this.getElement<HTMLButtonElement>('#moon-finish-cancel');
  private readonly moonFinishAttachButton = this.getElement<HTMLButtonElement>('#moon-finish-attach');
  private readonly moonOrbitUi = this.getElement<HTMLElement>('#moon-orbit-ui');
  private readonly moonOrbitBackButton = this.getElement<HTMLButtonElement>('#moon-orbit-back');
  private readonly moonOrbitResetButton = this.getElement<HTMLButtonElement>('#moon-orbit-reset');
  private readonly moonOrbitNewButton = this.getElement<HTMLButtonElement>('#moon-orbit-new');
  private readonly moonOrbitComplete = this.getElement<HTMLElement>('#moon-orbit-complete');
  private readonly moonOrbitSelectedName = this.getElement<HTMLElement>('#moon-orbit-selected-name');
  private readonly moonOrbitSelectedStats = this.getElement<HTMLElement>('#moon-orbit-selected-stats');
  private readonly moonOrbitCount = this.getElement<HTMLElement>('#moon-orbit-count');
  private readonly moonOrbitCapacity = this.getElement<HTMLElement>('#moon-orbit-capacity');
  private readonly moonOrbitEmpty = this.getElement<HTMLElement>('#moon-orbit-empty');
  private readonly moonOrbitEmptyLaterButton = this.getElement<HTMLButtonElement>('#moon-orbit-empty-later');
  private readonly moonOrbitEmptyAcceptButton = this.getElement<HTMLButtonElement>('#moon-orbit-empty-accept');
  private readonly moonOrbitFocus = this.getElement<HTMLElement>('#moon-orbit-focus');
  private readonly moonOrbitFocusName = this.getElement<HTMLElement>('#moon-orbit-focus-name');
  private readonly moonOrbitFocusStats = this.getElement<HTMLElement>('#moon-orbit-focus-stats');
  private readonly moonOrbitFocusBackButton = this.getElement<HTMLButtonElement>('#moon-orbit-focus-back');
  private readonly moonOrbitDeleteButton = this.getElement<HTMLButtonElement>('#moon-orbit-delete');
  private readonly moonDeleteModal = this.getElement<HTMLElement>('#moon-delete-modal');
  private readonly moonDeleteCancelButton = this.getElement<HTMLButtonElement>('#moon-delete-cancel');
  private readonly moonDeleteConfirmButton = this.getElement<HTMLButtonElement>('#moon-delete-confirm');
  private readonly moonResetModal = this.getElement<HTMLElement>('#moon-reset-modal');
  private readonly moonResetCancelButton = this.getElement<HTMLButtonElement>('#moon-reset-cancel');
  private readonly moonResetConfirmButton = this.getElement<HTMLButtonElement>('#moon-reset-confirm');
  private readonly fadeOverlay = this.getElement<HTMLElement>('#fade-overlay');
  private readonly halo: THREE.Mesh;
  private readonly stars: THREE.Points;
  private readonly lavaLight: THREE.PointLight;
  private readonly observationFill: THREE.HemisphereLight;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointerNdc = new THREE.Vector2();
  private readonly loop = new Loop(
    (delta) => this.update(delta),
    () => this.render(),
  );

  private readonly cameraGoal = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private frame = 0;
  private elapsed = 0;
  private lineIndex = 0;
  private stage: StoryStage = 'character';
  private fadeState: FadeState = 'idle';
  private fadeProgress = 0;
  private pendingStage: StoryStage | null = null;
  private cameraShake = 0;
  private ctaVisible = false;
  private pausedForScreenshot = false;
  private reducedMotion = false;
  private observationMode = false;
  private mainMenuActive = true;
  private mainMenuDialogueActive = false;
  private mainMenuChoicesVisible = false;
  private mainMenuChoicesUnlocked = true;
  private gameMode = false;
  private orbitMode = false;
  private orbitBannerTimer = 0;
  private orbitCapacityTimer = 0;
  private firstMoonTutorialActive = false;
  private readonly onAppPointerDown = (event: PointerEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest('#fullscreen-toggle')) return;
    if (
      !this.moonGameExitModal.hidden
      || !this.moonFinishModal.hidden
      || !this.moonDeleteModal.hidden
      || !this.moonResetModal.hidden
    ) return;
    if (this.orbitMode) {
      if (target instanceof Element && target.closest('#moon-orbit-ui button')) return;
      if (!this.moonOrbitEmpty.hidden) return;
      event.preventDefault();
      this.orbitScene.handlePointerDown(event);
      return;
    }
    if (this.gameMode) {
      if (target instanceof Element && target.closest('#moon-game-ui button')) return;
      event.preventDefault();
      this.sculpting.handlePointerDown(event);
      return;
    }
    if (this.observationMode) {
      if (
        target instanceof Element &&
        target.closest('#observation-back, #observation-reset, #observation-hotspots, #observation-feature-modal')
      ) {
        return;
      }
      event.preventDefault();
      this.observation.handlePointerDown(event);
      return;
    }
    if (this.mainMenuActive) {
      const targetElement = target instanceof Element ? target : null;
      if (targetElement?.closest('#main-menu-choices, #main-moon-button')) return;
      if (targetElement?.closest('#dialogue-panel')) {
        event.preventDefault();
        if (!this.mainMenuDialogueActive) return;
        if (this.dialogue.isTyping()) this.dialogue.finish();
        else this.showMainMenuChoices();
        return;
      }
      if (!this.mainMenuDialogueActive && this.isMoonPointer(event)) {
        event.preventDefault();
        this.beginMainMenuDialogue();
      }
      return;
    }
    if (target instanceof Element && target.closest('#observe-button')) return;
    event.preventDefault();
    this.advanceStory();
  };

  private readonly onAppPointerMove = (event: PointerEvent) => {
    if (event.target instanceof Element && event.target.closest('#fullscreen-toggle')) return;
    if (this.orbitMode) {
      if (!this.moonOrbitEmpty.hidden) return;
      event.preventDefault();
      this.orbitScene.handlePointerMove(event);
      return;
    }
    if (this.gameMode) {
      event.preventDefault();
      this.sculpting.handlePointerMove(event);
      return;
    }
    if (!this.observationMode) return;
    event.preventDefault();
    this.observation.handlePointerMove(event);
  };

  private readonly onAppPointerUp = (event: PointerEvent) => {
    if (event.target instanceof Element && event.target.closest('#fullscreen-toggle')) return;
    if (this.orbitMode) {
      if (!this.moonOrbitEmpty.hidden) return;
      event.preventDefault();
      this.orbitScene.handlePointerUp(event);
      this.updateMoonOrbitUi();
      return;
    }
    if (this.gameMode) {
      event.preventDefault();
      this.sculpting.handlePointerUp(event);
      return;
    }
    if (!this.observationMode) return;
    event.preventDefault();
    this.observation.handlePointerUp(event.pointerId);
  };

  private readonly onAppWheel = (event: WheelEvent) => {
    if (!this.observationMode) return;
    event.preventDefault();
    this.observation.handleWheel(event.deltaY);
  };

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (!this.moonGameExitModal.hidden && event.code === 'Escape') {
      event.preventDefault();
      this.closeMoonGameExitModal();
      return;
    }
    if (!this.moonResetModal.hidden && event.code === 'Escape') {
      event.preventDefault();
      this.closeMoonResetModal();
      return;
    }
    if (!this.moonDeleteModal.hidden && event.code === 'Escape') {
      event.preventDefault();
      this.closeMoonDeleteModal();
      return;
    }
    if (!this.moonFinishModal.hidden && event.code === 'Escape') {
      event.preventDefault();
      this.closeMoonFinishModal();
      return;
    }
    if (this.orbitMode) {
      if (event.code === 'Escape') {
        event.preventDefault();
        if (!this.moonOrbitEmpty.hidden) this.closeMoonOrbitEmpty();
        else if (this.orbitScene.getFocusedDesign()) this.exitMoonFocus();
        else this.enterMainMenu();
      }
      return;
    }
    if (this.gameMode) {
      if (event.code === 'Escape') {
        event.preventDefault();
        this.enterMainMenu();
      }
      return;
    }
    if (this.observationMode) {
      if (event.code === 'Escape') {
        event.preventDefault();
        if (!this.observationFeatureModal.hidden) this.closeObservationFeature();
        else this.exitObservationMode();
      }
      return;
    }
    if (this.mainMenuActive) {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      if (event.target instanceof HTMLButtonElement) return;
      if (!this.mainMenuDialogueActive) return;
      event.preventDefault();
      if (this.dialogue.isTyping()) this.dialogue.finish();
      else this.showMainMenuChoices();
      return;
    }
    if (event.code !== 'Space' && event.code !== 'Enter') return;
    if (event.target instanceof HTMLButtonElement) return;
    event.preventDefault();
    this.advanceStory();
  };

  private readonly onObserveClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.enterObservationMode();
  };

  private readonly onMainMoonClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.beginMainMenuDialogue();
  };

  private readonly onFormationChoiceClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.mainMenuChoicesUnlocked = true;
    this.updateMainMenuChoiceLocks();
    this.startFormationStory();
  };

  private readonly onObservationChoiceClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.enterObservationMode();
  };

  private readonly onGameChoiceClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.enterOrbitMode(null);
  };

  private readonly onMoonGameBackClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.moonGameExitModal.hidden = false;
    this.moonGameExitModal.setAttribute('aria-hidden', 'false');
    this.moonGameExitConfirmButton.focus();
  };

  private readonly onMoonGameExitCancelClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeMoonGameExitModal();
    this.moonGameBackButton.focus();
  };

  private readonly onMoonGameExitConfirmClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeMoonGameExitModal();
    this.sculpting.reset();
    this.firstMoonTutorialActive = false;
    this.enterMainMenu();
  };

  private readonly onMoonGameUndoClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.sculpting.undo();
    this.updateMoonGameUi();
  };

  private readonly onMoonGameRedoClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.sculpting.redo();
    this.updateMoonGameUi();
  };

  private readonly onMoonGameSizeClick = (event: MouseEvent) => {
    event.stopPropagation();
    const button = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('[data-meteor-size]')
      : null;
    const size = button?.dataset.meteorSize;
    if (size !== 'small' && size !== 'medium' && size !== 'large') return;
    this.sculpting.setMeteorSize(size satisfies MeteorSize);
    this.updateMoonGameUi();
  };

  private readonly onMoonGameEruptClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.sculpting.eruptSelected();
    this.updateMoonGameUi();
  };

  private readonly onMoonGameFinishClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.openMoonFinishModal();
  };

  private readonly onMoonFinishCancelClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeMoonFinishModal();
  };

  private readonly onMoonFinishAttachClick = (event: MouseEvent) => {
    event.stopPropagation();
    const draft = this.sculpting.exportMoonDesign();
    if (!draft) {
      this.closeMoonFinishModal();
      this.updateMoonGameUi();
      return;
    }
    const saved = this.moonCollection.add(draft);
    this.closeMoonFinishModal();
    if (saved) {
      this.firstMoonTutorialActive = false;
      this.enterOrbitMode(saved.id);
    }
    else {
      this.enterOrbitMode(null);
      this.showOrbitCapacityMessage();
    }
  };

  private readonly onMoonOrbitBackClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.enterMainMenu();
  };

  private readonly onMoonOrbitResetClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.moonResetModal.hidden = false;
    this.moonResetModal.setAttribute('aria-hidden', 'false');
    this.moonResetConfirmButton.focus();
  };

  private readonly onMoonResetCancelClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeMoonResetModal();
  };

  private readonly onMoonResetConfirmClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.moonCollection.clear();
    this.orbitScene.setMoons([]);
    this.orbitScene.resetView();
    this.closeMoonResetModal();
    this.updateMoonOrbitUi();
    this.showMoonOrbitEmpty();
  };

  private readonly onMoonOrbitNewClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (this.moonCollection.isFull()) {
      this.showOrbitCapacityMessage();
      return;
    }
    this.beginNewMoon();
  };

  private readonly onMoonOrbitEmptyLaterClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeMoonOrbitEmpty();
    this.moonOrbitNewButton.focus();
  };

  private readonly onMoonOrbitEmptyAcceptClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeMoonOrbitEmpty();
    this.beginNewMoon();
  };

  private readonly onMoonOrbitFocusBackClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.exitMoonFocus();
  };

  private readonly onMoonOrbitDeleteClick = (event: MouseEvent) => {
    event.stopPropagation();
    const focused = this.orbitScene.getFocusedDesign();
    if (!focused) return;
    this.moonDeleteModal.hidden = false;
    this.moonDeleteModal.setAttribute('aria-hidden', 'false');
    this.moonDeleteConfirmButton.focus();
  };

  private readonly onMoonDeleteCancelClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeMoonDeleteModal();
  };

  private readonly onMoonDeleteConfirmClick = (event: MouseEvent) => {
    event.stopPropagation();
    const focused = this.orbitScene.getFocusedDesign();
    if (!focused || !this.moonCollection.remove(focused.id)) {
      this.closeMoonDeleteModal();
      return;
    }
    this.closeMoonDeleteModal();
    this.orbitScene.setMoons(this.moonCollection.getAll());
    this.orbitScene.resetView();
    this.orbitCapacityTimer = 0;
    this.moonOrbitCapacity.hidden = true;
    this.updateMoonOrbitUi();
    if (this.moonCollection.getAll().length === 0) this.showMoonOrbitEmpty();
  };

  private readonly onObservationBackClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.exitObservationMode();
  };

  private readonly onObservationResetClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.observation.reset();
  };

  private readonly onObservationHotspotPointerDown = (event: PointerEvent) => {
    event.stopPropagation();
  };

  private readonly onObservationHotspotClick = (event: MouseEvent) => {
    event.stopPropagation();
    const target = event.target;
    const button = target instanceof Element ? target.closest<HTMLButtonElement>('[data-feature]') : null;
    const featureId = button?.dataset.feature;
    if (!button || !this.isObservationFeatureId(featureId)) return;
    this.openObservationFeature(featureId);
  };

  private readonly onObservationFeatureCloseClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.closeObservationFeature();
  };

  private readonly onObservationFeatureModalClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (event.target === this.observationFeatureModal) this.closeObservationFeature();
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.observation = new ObservationScene(this.renderer);
    this.sculpting = new MoonSculptingScene(this.camera, canvas);
    this.orbitScene = new MoonOrbitScene(this.camera, canvas);
    this.renderer.setClearColor('#081321', 1);
    this.renderer.toneMappingExposure = 1.08;

    this.lavaLight = new THREE.PointLight('#ff672d', 0, 12, 2);
    this.lavaLight.position.set(-0.55, 0.25, 3.1);
    this.observationFill = new THREE.HemisphereLight('#fff8df', '#3c4658', 0);

    this.halo = this.createHalo();
    this.stars = this.createStarfield();
    this.createScene();
    this.installInput();
    this.installTestHooks();
    this.enterMainMenu();
    const gameDprCap = this.gameMode && this.canvas.clientWidth <= 1024 ? 1.25 : 1.5;
    resizeRenderer(this.renderer, this.camera, gameDprCap);
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.app.removeEventListener('pointerdown', this.onAppPointerDown);
    this.app.removeEventListener('pointermove', this.onAppPointerMove);
    this.app.removeEventListener('pointerup', this.onAppPointerUp);
    this.app.removeEventListener('pointercancel', this.onAppPointerUp);
    this.app.removeEventListener('wheel', this.onAppWheel);
    window.removeEventListener('keydown', this.onKeyDown);
    this.observeButton.removeEventListener('click', this.onObserveClick);
    this.mainMenuMoonButton.removeEventListener('click', this.onMainMoonClick);
    this.formationChoiceButton.removeEventListener('click', this.onFormationChoiceClick);
    this.observationChoiceButton.removeEventListener('click', this.onObservationChoiceClick);
    this.gameChoiceButton.removeEventListener('click', this.onGameChoiceClick);
    this.moonGameBackButton.removeEventListener('click', this.onMoonGameBackClick);
    this.moonGameExitCancelButton.removeEventListener('click', this.onMoonGameExitCancelClick);
    this.moonGameExitConfirmButton.removeEventListener('click', this.onMoonGameExitConfirmClick);
    this.moonGameUndoButton.removeEventListener('click', this.onMoonGameUndoClick);
    this.moonGameRedoButton.removeEventListener('click', this.onMoonGameRedoClick);
    this.moonGameSizeControls.removeEventListener('click', this.onMoonGameSizeClick);
    this.moonGameEruptButton.removeEventListener('click', this.onMoonGameEruptClick);
    this.moonGameFinishButton.removeEventListener('click', this.onMoonGameFinishClick);
    this.moonFinishCancelButton.removeEventListener('click', this.onMoonFinishCancelClick);
    this.moonFinishAttachButton.removeEventListener('click', this.onMoonFinishAttachClick);
    this.moonOrbitBackButton.removeEventListener('click', this.onMoonOrbitBackClick);
    this.moonOrbitResetButton.removeEventListener('click', this.onMoonOrbitResetClick);
    this.moonOrbitNewButton.removeEventListener('click', this.onMoonOrbitNewClick);
    this.moonOrbitEmptyLaterButton.removeEventListener('click', this.onMoonOrbitEmptyLaterClick);
    this.moonOrbitEmptyAcceptButton.removeEventListener('click', this.onMoonOrbitEmptyAcceptClick);
    this.moonOrbitFocusBackButton.removeEventListener('click', this.onMoonOrbitFocusBackClick);
    this.moonOrbitDeleteButton.removeEventListener('click', this.onMoonOrbitDeleteClick);
    this.moonDeleteCancelButton.removeEventListener('click', this.onMoonDeleteCancelClick);
    this.moonDeleteConfirmButton.removeEventListener('click', this.onMoonDeleteConfirmClick);
    this.moonResetCancelButton.removeEventListener('click', this.onMoonResetCancelClick);
    this.moonResetConfirmButton.removeEventListener('click', this.onMoonResetConfirmClick);
    this.observationBackButton.removeEventListener('click', this.onObservationBackClick);
    this.observationResetButton.removeEventListener('click', this.onObservationResetClick);
    this.observationHotspots.removeEventListener('pointerdown', this.onObservationHotspotPointerDown);
    this.observationHotspots.removeEventListener('click', this.onObservationHotspotClick);
    this.observationFeatureClose.removeEventListener('click', this.onObservationFeatureCloseClick);
    this.observationFeatureModal.removeEventListener('click', this.onObservationFeatureModalClick);
    this.clearMeteors();
    this.clearBursts();
    disposeMeteorResources(this.meteorResources);
    this.moon.dispose();
    this.observation.dispose();
    this.sculpting.dispose();
    this.orbitScene.dispose();
    this.halo.geometry.dispose();
    (this.halo.material as THREE.Material).dispose();
    this.stars.geometry.dispose();
    (this.stars.material as THREE.Material).dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private update(delta: number): void {
    this.frame += 1;
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }

    this.elapsed += delta;
    const activeDprCap = (this.gameMode || this.orbitMode) && this.canvas.clientWidth <= 1024 ? 1.25 : 1.5;
    resizeRenderer(this.renderer, this.camera, activeDprCap);
    this.dialogue.update(delta);
    this.updateFade(delta);
    if (this.gameMode) {
      this.sculpting.update(delta, this.reducedMotion);
      this.updateMoonGameUi();
    } else if (this.orbitMode) {
      this.orbitScene.update(delta, this.reducedMotion);
      if (this.orbitBannerTimer > 0 && !this.orbitScene.getSnapshot().attaching) {
        this.orbitBannerTimer = Math.max(0, this.orbitBannerTimer - delta);
        this.moonOrbitComplete.hidden = this.orbitBannerTimer <= 0;
      }
      if (this.orbitCapacityTimer > 0) {
        this.orbitCapacityTimer = Math.max(0, this.orbitCapacityTimer - delta);
        this.moonOrbitCapacity.hidden = this.orbitCapacityTimer <= 0;
      }
      this.updateMoonOrbitUi();
    } else if (this.observationMode) {
      this.observation.update(delta, this.reducedMotion);
    } else {
      this.moon.update(delta, this.elapsed, !this.reducedMotion);
      this.updateMeteors(delta);
      this.updateBursts(delta);
    }
    this.updateCamera(delta);
    if (this.mainMenuActive) {
      this.updateMainMenuMoonButton();
      if (this.mainMenuDialogueActive && !this.mainMenuChoicesVisible && !this.dialogue.isTyping()) {
        this.showMainMenuChoices();
      }
    }
    if (this.observationMode) this.updateObservationFeatures();
    this.lavaLight.intensity = !this.observationMode && !this.gameMode && this.stage === 'lava' ? 1.05 + Math.sin(this.elapsed * 7) * 0.12 : 0;
    this.observationFill.intensity = this.observationMode ? 1.25 : 0;
    this.stars.rotation.y = this.reducedMotion ? 0 : this.elapsed * 0.004;
    this.halo.rotation.z = this.reducedMotion ? 0 : this.elapsed * 0.025;

    if (!this.observationMode && this.lineIndex === STORY.length - 1 && !this.dialogue.isTyping() && this.fadeState === 'idle') {
      this.showObservationCta();
    }
    this.publishDiagnostics();
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private createScene(): void {
    this.scene.background = new THREE.Color('#081321');
    this.scene.fog = new THREE.Fog('#081321', 20, 42);

    const hemisphere = new THREE.HemisphereLight('#e9f4ff', '#111a2a', 1.75);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight('#fff0c6', 3.15);
    sun.position.set(-4.5, 6.8, 7.2);
    this.scene.add(sun);

    const rim = new THREE.PointLight('#5e9bc5', 1.9, 20, 2);
    rim.position.set(4.5, 1.5, -2.8);
    this.scene.add(rim);

    this.scene.add(
      this.stars,
      this.halo,
      this.moon.group,
      this.observation.group,
      this.sculpting.group,
      this.orbitScene.group,
      this.lavaLight,
      this.observationFill,
    );
  }

  private createStarfield(): THREE.Points {
    const count = 210;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const colorA = new THREE.Color('#d6edf0');
    const colorB = new THREE.Color('#f5c86b');

    for (let index = 0; index < count; index += 1) {
      const angle = index * 2.399963;
      const radius = 8.5 + (index % 11) * 0.82;
      const height = ((index * 37) % 19) - 9.5;
      const offset = index * 3;
      positions[offset] = Math.cos(angle) * radius;
      positions[offset + 1] = height + Math.sin(angle * 0.7) * 1.2;
      positions[offset + 2] = -5 - (index % 7) * 1.7;

      const tint = index % 5 === 0 ? colorB : colorA;
      colors[offset] = tint.r;
      colors[offset + 1] = tint.g;
      colors[offset + 2] = tint.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      size: 0.075,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.78,
      depthWrite: false,
    });
    return new THREE.Points(geometry, material);
  }

  private createHalo(): THREE.Mesh {
    const geometry = new THREE.RingGeometry(2.6, 2.64, 64);
    const material = new THREE.MeshBasicMaterial({
      color: '#79b9cf',
      transparent: true,
      opacity: 0.18,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const halo = new THREE.Mesh(geometry, material);
    halo.position.z = -0.45;
    return halo;
  }

  private installInput(): void {
    this.app.addEventListener('pointerdown', this.onAppPointerDown, { passive: false });
    this.app.addEventListener('pointermove', this.onAppPointerMove, { passive: false });
    this.app.addEventListener('pointerup', this.onAppPointerUp, { passive: false });
    this.app.addEventListener('pointercancel', this.onAppPointerUp, { passive: false });
    this.app.addEventListener('wheel', this.onAppWheel, { passive: false });
    window.addEventListener('keydown', this.onKeyDown);
    this.observeButton.addEventListener('click', this.onObserveClick);
    this.mainMenuMoonButton.addEventListener('click', this.onMainMoonClick);
    this.formationChoiceButton.addEventListener('click', this.onFormationChoiceClick);
    this.observationChoiceButton.addEventListener('click', this.onObservationChoiceClick);
    this.gameChoiceButton.addEventListener('click', this.onGameChoiceClick);
    this.moonGameBackButton.addEventListener('click', this.onMoonGameBackClick);
    this.moonGameExitCancelButton.addEventListener('click', this.onMoonGameExitCancelClick);
    this.moonGameExitConfirmButton.addEventListener('click', this.onMoonGameExitConfirmClick);
    this.moonGameUndoButton.addEventListener('click', this.onMoonGameUndoClick);
    this.moonGameRedoButton.addEventListener('click', this.onMoonGameRedoClick);
    this.moonGameSizeControls.addEventListener('click', this.onMoonGameSizeClick);
    this.moonGameEruptButton.addEventListener('click', this.onMoonGameEruptClick);
    this.moonGameFinishButton.addEventListener('click', this.onMoonGameFinishClick);
    this.moonFinishCancelButton.addEventListener('click', this.onMoonFinishCancelClick);
    this.moonFinishAttachButton.addEventListener('click', this.onMoonFinishAttachClick);
    this.moonOrbitBackButton.addEventListener('click', this.onMoonOrbitBackClick);
    this.moonOrbitResetButton.addEventListener('click', this.onMoonOrbitResetClick);
    this.moonOrbitNewButton.addEventListener('click', this.onMoonOrbitNewClick);
    this.moonOrbitEmptyLaterButton.addEventListener('click', this.onMoonOrbitEmptyLaterClick);
    this.moonOrbitEmptyAcceptButton.addEventListener('click', this.onMoonOrbitEmptyAcceptClick);
    this.moonOrbitFocusBackButton.addEventListener('click', this.onMoonOrbitFocusBackClick);
    this.moonOrbitDeleteButton.addEventListener('click', this.onMoonOrbitDeleteClick);
    this.moonDeleteCancelButton.addEventListener('click', this.onMoonDeleteCancelClick);
    this.moonDeleteConfirmButton.addEventListener('click', this.onMoonDeleteConfirmClick);
    this.moonResetCancelButton.addEventListener('click', this.onMoonResetCancelClick);
    this.moonResetConfirmButton.addEventListener('click', this.onMoonResetConfirmClick);
    this.observationBackButton.addEventListener('click', this.onObservationBackClick);
    this.observationResetButton.addEventListener('click', this.onObservationResetClick);
    this.observationHotspots.addEventListener('pointerdown', this.onObservationHotspotPointerDown);
    this.observationHotspots.addEventListener('click', this.onObservationHotspotClick);
    this.observationFeatureClose.addEventListener('click', this.onObservationFeatureCloseClick);
    this.observationFeatureModal.addEventListener('click', this.onObservationFeatureModalClick);
  }

  private advanceStory(): void {
    if (this.fadeState !== 'idle' || this.ctaVisible) return;
    if (this.dialogue.isTyping()) {
      this.dialogue.finish();
      return;
    }
    if (this.lineIndex >= STORY.length - 1) return;
    this.applyLine(this.lineIndex + 1, true);
  }

  private applyLine(index: number, allowTransition: boolean): void {
    const line = STORY[index];
    if (!line) return;

    this.lineIndex = index;
    this.cta.hidden = true;
    this.ctaVisible = false;
    this.ctaNote.hidden = true;
    this.setEra(line);
    this.dialogue.start(line.text, index, STORY.length);

    const needsFade = allowTransition && this.stage === 'character' && line.stage === 'smooth';
    if (needsFade) {
      this.beginFade(line.stage);
    } else {
      this.setStage(line.stage);
    }
  }

  private setEra(line: StoryLine): void {
    this.eraKicker.textContent = line.kicker;
    this.eraValue.textContent = line.title;
  }

  private setStage(stage: StoryStage): void {
    this.stage = stage;
    // The ring is a friendly character accent, not part of the natural moon.
    this.halo.visible = stage === 'character';
    this.clearMeteors();

    if (stage === 'character') {
      this.moon.setMode('character');
      this.moon.setCraterCount(0);
      this.lavaLight.intensity = 0;
    } else if (stage === 'smooth') {
      this.moon.setMode('smooth');
      this.moon.setCraterCount(0);
      this.lavaLight.intensity = 0;
    } else if (stage === 'impacts') {
      this.moon.setMode('impacts');
      this.moon.setCraterCount(0);
      this.launchMeteors('formation');
    } else if (stage === 'lava') {
      const retainedCraterCount = Math.max(this.moon.getCraterCount(), FORMATION_CRATER_COUNT);
      this.moon.setMode('lava');
      this.moon.setCraterCount(retainedCraterCount);
    } else {
      this.moon.setMode('cratered');
      if (this.lineIndex === STORY.length - 1) {
        this.moon.setCraterCount(STORY_CRATER_COUNT);
      } else {
        this.moon.setCraterCount(Math.max(this.moon.getCraterCount(), FORMATION_CRATER_COUNT));
        this.launchMeteors('late');
      }
      this.lavaLight.intensity = 0;
    }
  }

  private beginFade(stage: StoryStage): void {
    this.pendingStage = stage;
    this.fadeState = 'out';
    this.fadeProgress = 0;
  }

  private updateFade(delta: number): void {
    if (this.fadeState === 'idle') {
      this.fadeOverlay.style.opacity = '0';
      return;
    }

    this.fadeProgress += delta / 0.42;
    if (this.fadeState === 'out') {
      this.fadeOverlay.style.opacity = String(THREE.MathUtils.clamp(this.fadeProgress, 0, 1));
      if (this.fadeProgress >= 1) {
        if (this.pendingStage) this.setStage(this.pendingStage);
        this.pendingStage = null;
        this.fadeState = 'in';
        this.fadeProgress = 0;
      }
    } else {
      this.fadeOverlay.style.opacity = String(1 - THREE.MathUtils.clamp(this.fadeProgress, 0, 1));
      if (this.fadeProgress >= 1) {
        this.fadeState = 'idle';
        this.fadeProgress = 0;
        this.fadeOverlay.style.opacity = '0';
      }
    }
  }

  private launchMeteors(wave: MeteorWave): void {
    const count = wave === 'formation' ? FORMATION_METEOR_COUNT : LATE_METEOR_COUNT;

    for (let index = 0; index < count; index += 1) {
      const targetIndex =
        wave === 'formation'
          ? index % FORMATION_CRATER_COUNT
          : FORMATION_CRATER_COUNT + (index % LATE_CRATER_REVEAL_COUNT);
      const band = index % 6;
      const row = Math.floor(index / 6);
      const distance = 5.25 + (index % 3) * 0.48 + row * 0.22;
      const lateral = (band - 2.5) * 0.28;
      const vertical = (row - 1) * 0.24 + (index % 2 === 0 ? -0.05 : 0.05);
      const target = getCraterTarget(targetIndex, this.moon.group.rotation.x, this.moon.group.rotation.y);
      const start = target
        .clone()
        .addScaledVector(METEOR_SHOWER_DIRECTION, -distance)
        .addScaledVector(METEOR_SHOWER_SIDE, lateral)
        .add(new THREE.Vector3(0, vertical, 0));
      const delay = index * (wave === 'formation' ? 0.095 : 0.12);
      const scale = wave === 'formation' ? 0.28 + (index % 5) * 0.035 : 0.3 + (index % 3) * 0.04;
      const duration = wave === 'formation' ? 0.8 + (index % 4) * 0.055 : 0.73 + (index % 3) * 0.05;
      const meteor = new Meteor(index, start, target, delay, scale, this.meteorResources, {
        duration,
        targetIndex,
        revealsCrater:
          wave === 'late' ? index < LATE_CRATER_REVEAL_COUNT : index < FORMATION_CRATER_COUNT,
      });
      this.meteors.push(meteor);
      this.scene.add(meteor.group);
    }
  }

  private updateMeteors(delta: number): void {
    for (let index = this.meteors.length - 1; index >= 0; index -= 1) {
      const meteor = this.meteors[index];
      const impacted = meteor.update(delta, this.reducedMotion);
      if (!impacted) continue;

      const normal = meteor.target.clone().normalize();
      if (meteor.revealsCrater) this.moon.revealNextCrater();
      this.cameraShake = 1;
      const burst = new ImpactBurst(meteor.target.clone().addScaledVector(normal, 0.02), normal, meteor.index);
      this.bursts.push(burst);
      this.scene.add(burst.group);
      this.scene.remove(meteor.group);
      meteor.dispose();
      this.meteors.splice(index, 1);
    }
  }

  private updateBursts(delta: number): void {
    for (let index = this.bursts.length - 1; index >= 0; index -= 1) {
      const burst = this.bursts[index];
      if (!burst.update(delta, this.reducedMotion)) continue;
      this.scene.remove(burst.group);
      burst.dispose();
      this.bursts.splice(index, 1);
    }
  }

  private clearMeteors(): void {
    for (const meteor of this.meteors) {
      this.scene.remove(meteor.group);
      meteor.dispose();
    }
    this.meteors.length = 0;
  }

  private clearBursts(): void {
    for (const burst of this.bursts) {
      this.scene.remove(burst.group);
      burst.dispose();
    }
    this.bursts.length = 0;
  }

  private updateCamera(delta: number): void {
    if (this.orbitMode) {
      this.orbitScene.applyCamera(delta);
      this.cameraShake = 0;
      return;
    }
    if (this.gameMode) {
      this.sculpting.applyCamera(delta, this.reducedMotion);
      this.cameraShake = 0;
      return;
    }
    if (this.observationMode) {
      this.cameraGoal.set(0, 0, this.observation.getCameraDistance(this.camera.aspect));
      const cameraSmoothing = 1 - Math.exp(-delta * 4.6);
      this.camera.position.lerp(this.cameraGoal, cameraSmoothing);
      this.cameraShake = 0;
      this.lookTarget.set(0, 0, 0);
      this.camera.lookAt(this.lookTarget);
      return;
    }

    const characterCamera = this.stage === 'character';
    const portrait = this.camera.aspect < 0.9;
    const characterDistance = portrait ? 12.2 : 9.65;
    const storyDistance = portrait ? STORY_CAMERA_DISTANCE * MOON_VIEW.portraitMultiplier : STORY_CAMERA_DISTANCE;
    this.cameraGoal.set(characterCamera ? 0 : 0.12, characterCamera ? 0.2 : 0.12, characterCamera ? characterDistance : storyDistance);
    const cameraSmoothing = 1 - Math.exp(-delta * 3.2);
    this.camera.position.lerp(this.cameraGoal, cameraSmoothing);

    this.cameraShake = Math.max(0, this.cameraShake - delta * 3.4);
    const shake = this.reducedMotion ? 0 : this.cameraShake * this.cameraShake * 0.1;
    this.camera.position.x += Math.sin(this.elapsed * 87) * shake;
    this.camera.position.y += Math.cos(this.elapsed * 71) * shake;
    this.lookTarget.set(0, characterCamera ? 0.03 : 0, 0);
    this.camera.lookAt(this.lookTarget);
  }

  private showObservationCta(): void {
    if (this.ctaVisible) return;
    this.ctaVisible = true;
    this.cta.hidden = false;
  }

  private enterMainMenu(): void {
    this.mainMenuActive = true;
    this.mainMenuDialogueActive = false;
    this.mainMenuChoicesVisible = false;
    this.gameMode = false;
    this.orbitMode = false;
    this.observationMode = false;
    this.lineIndex = 0;
    this.fadeState = 'idle';
    this.fadeProgress = 0;
    this.pendingStage = null;
    this.fadeOverlay.style.opacity = '0';

    this.mainMenuUi.hidden = false;
    this.mainMenuChoices.hidden = true;
    this.mainMenuMoonButton.hidden = false;
    this.mainMenuHint.textContent = '달을 클릭해 이야기를 시작해 보세요.';
    this.moonGameUi.hidden = true;
    this.moonOrbitUi.hidden = true;
    this.closeMoonOrbitEmpty();
    this.eraLabel.hidden = true;
    this.dialoguePanel.hidden = true;
    this.cta.hidden = true;
    this.ctaVisible = false;
    this.ctaNote.hidden = true;
    this.observationUi.hidden = true;
    this.observation.group.visible = false;
    this.sculpting.setActive(false);
    this.orbitScene.setActive(false);
    this.closeMoonGameExitModal();
    this.closeMoonFinishModal();
    this.closeMoonDeleteModal();
    this.closeObservationFeature();

    this.clearMeteors();
    this.clearBursts();
    this.halo.visible = true;
    this.moon.group.visible = true;
    this.setStage('character');
    this.dialogue.finish();
    this.dialogue.setHint('달을 클릭해 이야기를 시작해 보세요.');
    this.dialogue.setProgress('');
  }

  private beginMainMenuDialogue(): void {
    if (!this.mainMenuActive || this.mainMenuDialogueActive) return;

    this.mainMenuDialogueActive = true;
    this.mainMenuMoonButton.hidden = true;
    this.mainMenuHint.textContent = '달이 이야기를 들려줄게.';
    this.dialoguePanel.hidden = false;
    this.dialogue.start('안녕? 무슨 일이야?', 0, 1);
    this.dialogue.setProgress('');
  }

  private showMainMenuChoices(): void {
    if (!this.mainMenuActive || !this.mainMenuDialogueActive || this.mainMenuChoicesVisible) return;

    this.mainMenuChoicesVisible = true;
    this.mainMenuChoices.hidden = false;
    this.updateMainMenuChoiceLocks();
    this.dialogue.finish();
    this.dialogue.setHint('선택지 고르기');
    this.dialogue.setProgress('');
  }

  private updateMainMenuChoiceLocks(): void {
    const locked = !this.mainMenuChoicesUnlocked;
    for (const button of [this.observationChoiceButton, this.gameChoiceButton]) {
      button.disabled = locked;
      button.setAttribute('aria-disabled', String(locked));
      const lock = button.querySelector<HTMLElement>('.main-menu-choice-lock');
      if (lock) lock.hidden = !locked;
    }
  }

  private startFormationStory(): void {
    this.mainMenuActive = false;
    this.mainMenuDialogueActive = false;
    this.mainMenuChoicesVisible = false;
    this.gameMode = false;
    this.orbitMode = false;
    this.mainMenuUi.hidden = true;
    this.mainMenuChoices.hidden = true;
    this.mainMenuMoonButton.hidden = true;
    this.moonGameUi.hidden = true;
    this.moonOrbitUi.hidden = true;
    this.eraLabel.hidden = false;
    this.dialoguePanel.hidden = false;
    this.observationUi.hidden = true;
    this.observation.group.visible = false;
    this.sculpting.setActive(false);
    this.orbitScene.setActive(false);
    this.moon.group.visible = true;
    this.cta.hidden = true;
    this.ctaVisible = false;
    this.ctaNote.hidden = true;
    this.fadeState = 'idle';
    this.fadeProgress = 0;
    this.pendingStage = null;
    this.fadeOverlay.style.opacity = '0';
    this.clearMeteors();
    this.clearBursts();
    this.applyLine(0, false);
  }

  private enterGameMode(): void {
    if (this.gameMode) return;

    this.mainMenuActive = false;
    this.mainMenuDialogueActive = false;
    this.mainMenuChoicesVisible = false;
    this.gameMode = true;
    this.orbitMode = false;
    this.mainMenuUi.hidden = true;
    this.mainMenuChoices.hidden = true;
    this.mainMenuMoonButton.hidden = true;
    this.dialoguePanel.hidden = true;
    this.eraLabel.hidden = true;
    this.cta.hidden = true;
    this.ctaVisible = false;
    this.ctaNote.hidden = true;
    this.observationUi.hidden = true;
    this.moonGameUi.hidden = false;
    this.moonOrbitUi.hidden = true;
    this.closeMoonOrbitEmpty();
    this.closeMoonGameExitModal();
    this.observation.group.visible = false;
    this.orbitScene.setActive(false);
    this.clearMeteors();
    this.clearBursts();
    this.halo.visible = false;
    this.moon.group.visible = false;
    this.sculpting.setActive(true);
    this.updateMoonGameUi();
    this.moonGameBackButton.focus();
  }

  private updateMoonGameUi(): void {
    const snapshot = this.sculpting.getSnapshot();
    this.updateFirstMoonTutorial(snapshot);
    const statusByState: Record<typeof snapshot.state, string> = {
      ready: '운석을 뒤로 당겨 달을 향해 놓아 보세요.',
      aiming: '노란 조준 표시가 원하는 곳에 오면 손을 놓아 발사하세요.',
      flying: '운석이 날아가고 있어요!',
      reloading: '다음 운석을 준비하고 있어요…',
    };
    const fillPercent = Math.round(snapshot.lavaProgress * 100);
    this.moonGameStatus.textContent = snapshot.selectedCraterState === 'erupting'
      ? `용암이 천천히 퍼지고 있어요 · ${fillPercent}%에서 멈출 수 있어요.`
      : snapshot.selectedCraterState === 'cooling'
        ? `용암이 ${fillPercent}%만큼 채워진 채로 식고 있어요.`
        : snapshot.selectedCraterState === 'mare'
          ? `용암이 ${fillPercent}%만큼 굳어 새로운 달의 바다가 되었어요.`
          : snapshot.canErupt
            ? '선택한 충돌 구덩이에 용암을 분출할 수 있어요.'
            : snapshot.activeProjectileCount > 0
              ? `운석 ${snapshot.activeProjectileCount}개가 날아가고 있어요. 다음 운석도 바로 당겨 보세요!`
              : statusByState[snapshot.state];
    this.moonGameCraterCount.textContent = `충돌 구덩이 ${snapshot.craterCount} · 달의 바다 ${snapshot.mareCount}`;
    this.moonGameFinishButton.disabled = !snapshot.canFinalize;
    this.moonGameFinishButton.title = snapshot.canFinalize
      ? '완성한 달을 지구 궤도에 붙이기'
      : snapshot.activeLavaCount > 0
        ? '용암이 모두 식으면 달을 완성할 수 있어요.'
        : snapshot.craterCount === 0
          ? '운석을 한 번 이상 충돌시켜 주세요.'
          : '현재 움직임이 끝나면 달을 완성할 수 있어요.';
    this.moonGameUndoButton.disabled = !snapshot.canUndo;
    const undoTarget = snapshot.undoKind === 'lava' ? '용암 분출' : snapshot.undoKind === 'impact' ? '운석 충돌' : '';
    this.moonGameUndoButton.title = undoTarget ? `${undoTarget} 되돌리기` : '되돌릴 작업이 없어요';
    this.moonGameUndoButton.setAttribute('aria-label', this.moonGameUndoButton.title);
    this.moonGameRedoButton.disabled = !snapshot.canRedo;
    const redoTarget = snapshot.redoKind === 'lava' ? '용암 분출' : snapshot.redoKind === 'impact' ? '운석 충돌' : '';
    this.moonGameRedoButton.title = redoTarget ? `${redoTarget} 다시 실행` : '다시 실행할 작업이 없어요';
    this.moonGameRedoButton.setAttribute('aria-label', this.moonGameRedoButton.title);
    this.moonGameEruptButton.hidden = snapshot.lavaAction === null;
    this.moonGameEruptButton.disabled = snapshot.lavaAction === null;
    this.moonGameEruptButton.dataset.mode = snapshot.lavaAction ?? '';
    this.moonGameEruptButton.lastChild!.textContent = snapshot.lavaAction === 'stop' ? ' 멈추기' : ' 용암 분출하기';
    for (const button of this.moonGameSizeControls.querySelectorAll<HTMLButtonElement>('[data-meteor-size]')) {
      button.setAttribute('aria-pressed', String(button.dataset.meteorSize === snapshot.meteorSize));
      button.disabled = snapshot.state === 'aiming';
    }
  }

  private updateFirstMoonTutorial(snapshot: ReturnType<MoonSculptingScene['getSnapshot']>): void {
    this.moonGameGuide.hidden = !this.firstMoonTutorialActive;
    if (!this.firstMoonTutorialActive) {
      delete this.moonGameUi.dataset.tutorialStep;
      return;
    }

    const step = snapshot.mareCount > 0 ? 3 : snapshot.craterCount > 0 ? 2 : 1;
    this.moonGameUi.dataset.tutorialStep = String(step);
    this.moonGameGuideNumber.textContent = String(step);
    if (step === 1) {
      this.moonGameGuideTitle.textContent = '운석을 날려보고';
      this.moonGameGuideText.textContent = '운석을 뒤로 당긴 뒤 손을 놓아 보세요.';
    } else if (step === 2) {
      this.moonGameGuideTitle.textContent = '용암을 채워보고';
      this.moonGameGuideText.textContent = '충돌 구덩이를 누른 뒤 용암을 분출하고 멈춰 보세요.';
    } else {
      this.moonGameGuideTitle.textContent = '달을 완성할 차례!';
      this.moonGameGuideText.textContent = '다 꾸몄으면 달 완성하기 버튼을 눌러!';
    }
  }

  private openMoonFinishModal(): void {
    const snapshot = this.sculpting.getSnapshot();
    if (!snapshot.canFinalize) return;
    this.moonFinishSummary.textContent = `충돌 구덩이 ${snapshot.craterCount}개 · 달의 바다 ${snapshot.mareCount}개`;
    this.moonFinishModal.hidden = false;
    this.moonFinishModal.setAttribute('aria-hidden', 'false');
    this.moonFinishAttachButton.focus();
  }

  private closeMoonFinishModal(): void {
    this.moonFinishModal.hidden = true;
    this.moonFinishModal.setAttribute('aria-hidden', 'true');
  }

  private closeMoonGameExitModal(): void {
    this.moonGameExitModal.hidden = true;
    this.moonGameExitModal.setAttribute('aria-hidden', 'true');
  }

  private enterOrbitMode(animateMoonId: string | null = null): void {
    this.mainMenuActive = false;
    this.mainMenuDialogueActive = false;
    this.mainMenuChoicesVisible = false;
    this.gameMode = false;
    this.observationMode = false;
    this.orbitMode = true;
    this.mainMenuUi.hidden = true;
    this.mainMenuChoices.hidden = true;
    this.mainMenuMoonButton.hidden = true;
    this.dialoguePanel.hidden = true;
    this.eraLabel.hidden = true;
    this.cta.hidden = true;
    this.ctaVisible = false;
    this.ctaNote.hidden = true;
    this.observationUi.hidden = true;
    this.moonGameUi.hidden = true;
    this.moonOrbitUi.hidden = false;
    this.observation.group.visible = false;
    this.sculpting.setActive(false);
    this.moon.group.visible = false;
    this.halo.visible = false;
    this.clearMeteors();
    this.clearBursts();
    this.orbitScene.setMoons(this.moonCollection.getAll(), animateMoonId);
    this.orbitScene.setActive(true);
    this.orbitScene.resetView();
    this.orbitBannerTimer = animateMoonId ? 3.2 : 0;
    this.orbitCapacityTimer = 0;
    this.moonOrbitComplete.hidden = !animateMoonId;
    this.moonOrbitCapacity.hidden = true;
    this.closeMoonGameExitModal();
    this.closeMoonDeleteModal();
    this.closeMoonResetModal();
    this.updateMoonOrbitUi();
    if (this.moonCollection.getAll().length === 0) this.showMoonOrbitEmpty();
    else this.moonOrbitNewButton.focus();
  }

  private updateMoonOrbitUi(): void {
    const moons = this.moonCollection.getAll();
    const selected = this.orbitScene.getSelectedDesign() ?? moons.at(-1) ?? null;
    const focused = this.orbitScene.getFocusedDesign();
    const full = this.moonCollection.isFull();
    this.moonOrbitSelectedName.textContent = selected?.name ?? '아직 만든 달이 없어요';
    this.moonOrbitSelectedStats.textContent = selected
      ? `충돌 구덩이 ${selected.craterCount}개 · 달의 바다 ${selected.mareCount}개`
      : '달을 만들어 지구 궤도에 붙여 보세요.';
    this.moonOrbitCount.textContent = full
      ? `달 ${moons.length}/${MAX_ORBIT_MOONS}개 · 달을 선택하면 교체할 수 있어요.`
      : `달 ${moons.length}/${MAX_ORBIT_MOONS}개가 지구 주위를 돌고 있어요.`;
    this.moonOrbitNewButton.textContent = full
      ? '달 교체하기'
      : moons.length === 0
        ? '첫 달 만들기'
        : '달 하나 더 만들기';
    this.moonOrbitNewButton.title = full ? '공전하는 달을 선택해 삭제한 뒤 새 달을 만들 수 있어요.' : '';
    this.moonOrbitUi.dataset.focused = String(Boolean(focused));
    this.moonOrbitFocus.hidden = !focused;
    if (focused) {
      this.moonOrbitFocusName.textContent = focused.name;
      this.moonOrbitFocusStats.textContent = `충돌 구덩이 ${focused.craterCount}개 · 달의 바다 ${focused.mareCount}개`;
    }
  }

  private exitMoonFocus(): void {
    this.orbitScene.exitFocus();
    this.updateMoonOrbitUi();
    this.moonOrbitResetButton.focus();
  }

  private closeMoonDeleteModal(): void {
    this.moonDeleteModal.hidden = true;
    this.moonDeleteModal.setAttribute('aria-hidden', 'true');
  }

  private closeMoonResetModal(): void {
    this.moonResetModal.hidden = true;
    this.moonResetModal.setAttribute('aria-hidden', 'true');
  }

  private beginNewMoon(): void {
    this.firstMoonTutorialActive = this.moonCollection.getAll().length === 0;
    this.sculpting.reset();
    this.enterGameMode();
  }

  private showMoonOrbitEmpty(): void {
    this.moonOrbitEmpty.hidden = false;
    this.moonOrbitEmpty.setAttribute('aria-hidden', 'false');
    this.moonOrbitEmptyAcceptButton.focus();
  }

  private closeMoonOrbitEmpty(): void {
    this.moonOrbitEmpty.hidden = true;
    this.moonOrbitEmpty.setAttribute('aria-hidden', 'true');
  }

  private showOrbitCapacityMessage(): void {
    this.orbitCapacityTimer = 4.5;
    this.moonOrbitCapacity.hidden = false;
  }

  private updateMainMenuMoonButton(): void {
    const visible = this.mainMenuActive && !this.mainMenuDialogueActive;
    this.mainMenuMoonButton.hidden = !visible;
  }

  private isMoonPointer(event: PointerEvent): boolean {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    this.pointerNdc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.camera.updateMatrixWorld();
    this.moon.group.updateWorldMatrix(true, true);
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    return this.raycaster.intersectObject(this.moon.group, true).length > 0;
  }

  private enterObservationMode(): void {
    if (this.observationMode) return;

    this.mainMenuActive = false;
    this.mainMenuDialogueActive = false;
    this.mainMenuChoicesVisible = false;
    this.gameMode = false;
    this.orbitMode = false;
    this.mainMenuUi.hidden = true;
    this.mainMenuChoices.hidden = true;
    this.mainMenuMoonButton.hidden = true;
    this.moonGameUi.hidden = true;
    this.moonOrbitUi.hidden = true;
    this.observationMode = true;
    this.ctaVisible = false;
    this.cta.hidden = true;
    this.ctaNote.hidden = true;
    this.eraLabel.hidden = true;
    this.dialoguePanel.hidden = true;
    this.observationUi.hidden = false;
    this.observationStatus.textContent = '달 표면 자료를 불러오는 중…';
    this.closeObservationFeature();

    this.clearMeteors();
    this.clearBursts();
    this.halo.visible = false;
    this.moon.group.visible = false;
    this.sculpting.setActive(false);
    this.orbitScene.setActive(false);
    this.observation.reset();
    this.observation.group.visible = true;

    void this.observation.load().then((ready) => {
      if (!this.observationMode) return;
      this.observationStatus.textContent = ready
        ? '드래그해서 돌려 보고, 두 손가락으로 확대해 보세요.'
        : '달 표면 자료를 불러오지 못했어요. 새로고침 후 다시 시도해 주세요.';
      this.updateObservationFeatures();
    });
  }

  private exitObservationMode(): void {
    if (!this.observationMode) return;

    this.observationMode = false;
    this.observation.group.visible = false;
    this.observationUi.hidden = true;
    this.closeObservationFeature();
    this.enterMainMenu();
  }

  private updateObservationFeatures(): void {
    if (!this.observationMode) {
      for (const feature of OBSERVATION_FEATURES) {
        const button = this.observationHotspots.querySelector<HTMLButtonElement>(`[data-feature="${feature.id}"]`);
        if (button) button.hidden = true;
      }
      return;
    }

    const width = this.observationUi.clientWidth || this.canvas.clientWidth;
    const height = this.observationUi.clientHeight || this.canvas.clientHeight;
    const ready = this.observation.getSnapshot().status === 'ready';

    for (const anchor of this.observation.getFeatureAnchors(this.camera, width, height)) {
      const button = this.observationHotspots.querySelector<HTMLButtonElement>(`[data-feature="${anchor.id}"]`);
      if (!button) continue;
      button.hidden = !ready || !anchor.visible;
      button.style.left = `${anchor.x}px`;
      button.style.top = `${anchor.y}px`;
    }
  }

  private openObservationFeature(featureId: ObservationFeatureId): void {
    const feature = OBSERVATION_FEATURES.find((candidate) => candidate.id === featureId);
    if (!feature || !this.observation.drawFeaturePreview(featureId, this.observationFeaturePreview)) return;

    this.observationFeatureTitle.textContent = feature.title;
    this.observationFeatureDialogue.textContent = feature.dialogue;
    this.observationFeatureModal.hidden = false;
    this.observationFeatureModal.setAttribute('aria-hidden', 'false');
    this.observationFeatureClose.focus();
  }

  private closeObservationFeature(): void {
    this.observationFeatureModal.hidden = true;
    this.observationFeatureModal.setAttribute('aria-hidden', 'true');
  }

  private isObservationFeatureId(value: string | undefined): value is ObservationFeatureId {
    return OBSERVATION_FEATURES.some((feature) => feature.id === value);
  }

  private createTestOrbit(count: number): void {
    this.moonCollection.clear();
    if (!this.mainMenuActive) this.enterMainMenu();
    this.enterGameMode();
    for (let index = 0; index < count; index += 1) {
      if (index === 0) this.sculpting.createDemoMareImpact();
      else this.sculpting.createDemoDepthVariety();
      const draft = this.sculpting.exportMoonDesign();
      if (draft) this.moonCollection.add(draft);
    }
    this.enterOrbitMode(null);
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        void value;
      },
      setState: (name: string) => {
        if (name === 'main-menu-choices') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.beginMainMenuDialogue();
          this.dialogue.finish();
          this.showMainMenuChoices();
          return;
        }
        if (name === 'game' || name === 'game-active') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          return;
        }
        if (name === 'game-cratered' || name === 'game-lava') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createDemoCrater(name === 'game-lava');
          this.updateMoonGameUi();
          return;
        }
        if (name === 'game-depth-variety') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createDemoDepthVariety();
          this.updateMoonGameUi();
          return;
        }
        if (name === 'game-stress') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createStressCraters();
          this.updateMoonGameUi();
          return;
        }
        if (name === 'game-mare-impact') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createDemoMareImpact();
          this.updateMoonGameUi();
          return;
        }
        if (name === 'game-mare-patches') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createDemoMarePatches();
          this.updateMoonGameUi();
          return;
        }
        if (name === 'game-mare-transition') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createDemoMareTransition();
          this.updateMoonGameUi();
          return;
        }
        if (name === 'game-lava-concurrent') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createDemoConcurrentLava();
          this.updateMoonGameUi();
          return;
        }
        if (name === 'game-finalizable') {
          if (!this.mainMenuActive) this.enterMainMenu();
          this.enterGameMode();
          this.sculpting.createDemoMareImpact();
          this.updateMoonGameUi();
          return;
        }
        if (name === 'moon-orbit' || name === 'moon-orbit-two' || name === 'moon-orbit-full') {
          this.createTestOrbit(name === 'moon-orbit-full' ? MAX_ORBIT_MOONS : name === 'moon-orbit-two' ? 2 : 1);
          return;
        }
        if (name === 'moon-orbit-focus') {
          this.createTestOrbit(2);
          this.orbitScene.focusSelected();
          this.updateMoonOrbitUi();
          return;
        }
        if (name === 'moon-orbit-saved') {
          this.enterOrbitMode(null);
          return;
        }
        if (name === 'observation') {
          this.jumpToLine(STORY.length - 1);
          this.dialogue.finish();
          this.showObservationCta();
          this.enterObservationMode();
          return;
        }
        if (this.observationMode) this.exitObservationMode();
        if (this.gameMode) this.enterMainMenu();
        if (this.mainMenuActive) this.startFormationStory();
        const index = STAGE_LINE_INDEX[name];
        if (index === undefined) {
          console.warn(`Unknown test state: ${name}`);
          return;
        }
        this.jumpToLine(index);
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
      },
      hideDebugUi: (hidden: boolean) => {
        void hidden;
      },
    };
  }

  private jumpToLine(index: number): void {
    if (this.orbitMode) this.enterMainMenu();
    if (this.gameMode) this.enterMainMenu();
    if (this.mainMenuActive) this.startFormationStory();
    this.fadeState = 'idle';
    this.fadeProgress = 0;
    this.pendingStage = null;
    this.fadeOverlay.style.opacity = '0';
    this.clearBursts();
    this.applyLine(THREE.MathUtils.clamp(index, 0, STORY.length - 1), false);
    if (index === STORY.length - 1) {
      this.dialogue.finish();
      this.showObservationCta();
    }
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      score: this.lineIndex,
      targetScore: STORY.length - 1,
      complete: this.ctaVisible || this.observationMode,
      stage: this.orbitMode
        ? 'orbit'
        : this.gameMode
          ? 'game'
          : this.observationMode
            ? 'observation'
            : this.mainMenuActive
              ? 'menu'
              : this.stage,
      mainMenu: this.mainMenuActive,
      gamePlaceholder: false,
      sculpting: this.sculpting.getSnapshot(),
      orbit: {
        ...this.orbitScene.getSnapshot(),
        storedMoonCount: this.moonCollection.getAll().length,
      },
      lineIndex: this.lineIndex,
      typing: this.dialogue.isTyping(),
      player: {
        position: {
          x: this.moon.group.position.x,
          y: this.moon.group.position.y,
          z: this.moon.group.position.z,
        },
        speed: 0,
      },
      moon: {
        position: {
          x: this.moon.group.position.x,
          y: this.moon.group.position.y,
          z: this.moon.group.position.z,
        },
        rotation: {
          yaw: this.moon.group.rotation.y,
          pitch: this.moon.group.rotation.x,
        },
        mode: this.moon.getMode(),
        craterCount: this.moon.getCraterCount(),
        lavaFlowProgress: this.moon.getLavaFlowProgress(),
        lavaCoolingProgress: this.moon.getLavaCoolingProgress(),
        lavaSourceCount: this.moon.getLavaSourceCount(),
        activeLavaSourceCount: this.moon.getActiveLavaSourceCount(),
        activeLavaCraterCount: this.moon.getActiveLavaCraterCount(),
      },
      observation: {
        active: this.observationMode,
        ...this.observation.getSnapshot(),
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      meteorCount: this.meteors.length,
      meteorTargetIndices: this.meteors.map((meteor) => meteor.targetIndex),
      burstCount: this.bursts.length,
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: this.canvas.clientWidth > 0 ? this.canvas.width / this.canvas.clientWidth : 1,
      },
    };
  }

  private getElement<T extends HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }
}
